/**
 * Страница регистрации.
 * Форма для создания нового пользователя (пациента или врача).
 * Для пациентов отображаются дополнительные поля (дата рождения, дата диагноза).
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'patient',
    birth_date: '',
    diagnosis_date: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = (): string | null => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(formData.email)) {
      return 'Некорректный формат email';
    }
    if (formData.password.length < 8) {
      return 'Пароль должен содержать минимум 8 символов';
    }
    if (!/[A-Z]/.test(formData.password)) {
      return 'Пароль должен содержать хотя бы одну заглавную букву';
    }
    if (!/[a-z]/.test(formData.password)) {
      return 'Пароль должен содержать хотя бы одну строчную букву';
    }
    if (!/[0-9]/.test(formData.password)) {
      return 'Пароль должен содержать хотя бы одну цифру';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      await register(formData);
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Регистрация</h1>

        <form onSubmit={handleSubmit}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Имя</label>
              <input
                name="first_name" value={formData.first_name}
                onChange={handleChange} style={styles.input}
                placeholder="Иван" required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Фамилия</label>
              <input
                name="last_name" value={formData.last_name}
                onChange={handleChange} style={styles.input}
                placeholder="Петров" required
              />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email" name="email" value={formData.email}
              onChange={handleChange} style={styles.input}
              placeholder="example@mail.ru" required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Пароль</label>
            <input
              type="password" name="password" value={formData.password}
              onChange={handleChange} style={styles.input}
              placeholder="Мин. 8 символов, A-Z, a-z, 0-9" required minLength={8}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Роль</label>
            <select
              name="role" value={formData.role}
              onChange={handleChange} style={styles.input}
            >
              <option value="patient">Пациент</option>
              <option value="doctor">Врач</option>
            </select>
          </div>

          {/* Дополнительные поля для пациента */}
          {formData.role === 'patient' && (
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Дата рождения</label>
                <input
                  type="date" name="birth_date" value={formData.birth_date}
                  onChange={handleChange} style={styles.input}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Дата диагноза</label>
                <input
                  type="date" name="diagnosis_date" value={formData.diagnosis_date}
                  onChange={handleChange} style={styles.input}
                />
              </div>
            </div>
          )}

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Регистрация...' : 'Зарегистрироваться'}
          </button>
        </form>

        <p style={styles.loginLink}>
          Уже есть аккаунт? <Link to="/login">Войти</Link>
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--bg-body)',
  },
  card: {
    backgroundColor: 'var(--bg-card)',
    padding: 40,
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    width: 480,
    maxWidth: '90vw',
  },
  title: {
    textAlign: 'center',
    color: '#2563eb',
    marginBottom: 24,
    fontSize: 24,
  },
  error: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '10px 14px',
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  row: { display: 'flex', gap: 12 },
  field: { marginBottom: 16, flex: 1 },
  label: {
    display: 'block',
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid var(--border-color)',
    borderRadius: 8,
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  loginLink: {
    textAlign: 'center',
    marginTop: 16,
    fontSize: 14,
    color: 'var(--text-secondary)',
  },
};

export default RegisterPage;
